/**
 * Phone number utility functions for parsing and formatting
 * Centralizes phone number handling logic used across components
 */

/**
 * Parses a phone number by removing the country code prefix if present
 * @param phone - The phone number to parse (may include non-digit characters)
 * @param countryCode - The country code to remove (e.g., "+65", "+1")
 * @returns The phone number without country code prefix
 */
export const parsePhoneNumber = (
  phone: string,
  countryCode: string
): string => {
  if (!phone) return "";

  const cleaned = phone.replace(/\D/g, "");
  const codeDigits = countryCode.replace("+", "");

  if (cleaned.startsWith(codeDigits)) {
    return cleaned.substring(codeDigits.length);
  }

  return cleaned;
};

/**
 * Formats a phone number with country code prefix
 * @param phone - The phone number to format
 * @param countryCode - The country code to prepend (e.g., "+65", "+1")
 * @returns The formatted phone number with country code
 */
export const formatPhoneWithCountry = (
  phone: string,
  countryCode: string
): string => {
  if (!phone) return "";

  const parsedNumber = parsePhoneNumber(phone, countryCode);
  return `${countryCode}${parsedNumber}`;
};

/**
 * Splits a full phone number into country code and number
 * Handles various international formats (+, 00 prefix)
 * @param fullNumber - Full phone number to split
 * @returns Object with countryCode and number, or "-" if not found
 */
export const splitPhoneNumber = (
  fullNumber: string | undefined
): { countryCode: string; number: string } => {
  if (!fullNumber) return { countryCode: "-", number: "-" };

  // Remove any spaces, dashes, or parentheses
  const cleaned = fullNumber.replace(/[\s\-\(\)]/g, "");

  // Check for international format with +
  if (cleaned.startsWith("+")) {
    // Try 3-digit codes first (for specific countries)
    const threeDigitMatch = cleaned.match(/^\+(\d{3})(\d+)$/);
    if (
      threeDigitMatch &&
      ["673", "852", "853", "886"].includes(threeDigitMatch[1])
    ) {
      return {
        countryCode: `+${threeDigitMatch[1]}`,
        number: threeDigitMatch[2],
      };
    }

    // Try 2-digit codes
    const twoDigitMatch = cleaned.match(/^\+(\d{2})(\d+)$/);
    if (twoDigitMatch) {
      return {
        countryCode: `+${twoDigitMatch[1]}`,
        number: twoDigitMatch[2],
      };
    }

    // Try 1-digit codes
    const oneDigitMatch = cleaned.match(/^\+(\d{1})(\d+)$/);
    if (oneDigitMatch) {
      return {
        countryCode: `+${oneDigitMatch[1]}`,
        number: oneDigitMatch[2],
      };
    }
  }

  // Check for international format with 00
  if (cleaned.startsWith("00")) {
    const twoDigitMatch = cleaned.match(/^00(\d{2})(\d+)$/);
    if (twoDigitMatch) {
      return {
        countryCode: `+${twoDigitMatch[1]}`,
        number: twoDigitMatch[2],
      };
    }
  }

  // If no pattern matches, assume it's just a number without country code
  return {
    countryCode: "-",
    number: cleaned,
  };
};
